import {
  Advice,
  ADVICE_NAMES,
  InsuranceAdvice,
  UserAuthorizationLevel,
} from "Store/advice/interfaces";
import {
  AdviceCard,
  AdviceDiff,
  AdviceDiffCard,
  AdviceIntroMessage,
  AdviceScreenshotData,
  AdviceTotalPriceCard,
  OneTimePayment,
} from "./interfaces";

import { getCorrectAdviceValues } from "Store/advice/productPriceRequestMappers";
import { getCorrectPayment } from "Store/advice/utils";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { isDefined } from "Utils/functions";
import { getCorrectPaymentValue } from "../AdviceCards/utils";

export const generateScreenshot = (
  adviceValues: Advice,
  ref?: HTMLDivElement | null,
): AdviceScreenshotData | null => {
  if (ref === null || typeof ref === "undefined") {
    return null;
  }
  return {
    adviceResponse: adviceValues,
    adviceIntroTexts: extractTextsByClassOrTagName("AdviceIntroText", ref),
    adviceIntroMessages: extractAdviceIntroMessages(ref),
    adviceCards: extractAdviceCardsData(ref, adviceValues),
    adviceDiffCard: extractAdviceDiffCardData(ref),
    adviceTotalPriceCard: extractAdviceTotalPriceCardData(ref),
  };
};

const extractTextsByClassOrTagName = (
  name: string,
  ref?: HTMLDivElement | Element,
  tag = false,
): string[] => {
  if (!ref) {
    return [];
  }
  const list = tag
    ? ref.getElementsByTagName(name)
    : ref.getElementsByClassName(name);
  const texts: string[] = [];
  if (list) {
    Array.from(list).forEach((element) => {
      const text = element.textContent;
      if (text) {
        texts.push(text);
      }
    });
  }
  return texts;
};

const extractAdviceIntroMessages = (
  ref: HTMLDivElement,
): AdviceIntroMessage[] => {
  const adviceIntroMessages = getFirst(
    "AdviceIntroMessages",
    ref,
  )?.getElementsByTagName("div");
  return Array.from(adviceIntroMessages ?? []).map((message) => ({
    title: getFirst("AdviceMessageTitle", message)?.textContent ?? "",
    text: getFirst("AdviceMessageText", message)?.textContent ?? "",
  }));
};

const extractAdviceCardsData = (
  ref: HTMLDivElement,
  adviceValues: Advice,
): AdviceCard[] => {
  return [
    ...mapAdviceCard(
      INSURANCE_NAME_VALUE.DEATH,
      adviceValues.advice.DEATH,
      getFirst("AdviceCard_DEATH", ref),
    ),
    ...mapAdviceCard(
      INSURANCE_NAME_VALUE.DISABILITY_FLEX,
      adviceValues.advice.DISABILITY_FLEX,
      getFirst("AdviceCard_DISABILITY_FLEX", ref),
    ),
    ...mapAdviceCard(
      INSURANCE_NAME_VALUE.CRITICAL_ILLNESS,
      adviceValues.advice.CRITICAL_ILLNESS,
      getFirst("AdviceCard_CRITICAL_ILLNESS", ref),
    ),
  ];
};

const mapAdviceCard = (
  insurance: ADVICE_NAMES,
  insuranceAdvice?: InsuranceAdvice,
  adviceCard?: Element,
): AdviceCard[] => {
  if (!adviceCard) {
    return [];
  }
  const adviceCardHeader = getFirst("AdviceCardHeader", adviceCard);

  const insuranceCheckbox = getFirst("InsuranceCheckbox", adviceCardHeader);
  const insuranceChecked = extractCheckboxValue(
    "InsuranceCheckbox",
    adviceCardHeader,
  );
  const insuranceName =
    getFirst("label", insuranceCheckbox, true)?.lastElementChild?.textContent ??
    "";
  const insuranceMonthlyPrice = Number(
    (
      getFirst("InsuranceMonthlyPrice", adviceCardHeader)?.textContent ?? "0"
    ).replace(/\D+/g, ""),
  );
  const adviceCardContent = getFirst("AdviceCardContent", adviceCard);
  const adviceHeaderText =
    getFirst("p", getFirst("AdviceHeaderText", adviceCardContent), true)
      ?.textContent ?? "";
  const adviceTexts = extractTextsByClassOrTagName(
    "p",
    getFirst("AdviceTexts", adviceCardContent),
    true,
  );

  const advicedSliderValue = getCorrectPaymentValue(
    UserAuthorizationLevel.CUSTOMER,
    insuranceAdvice ? getCorrectPayment(insuranceAdvice) : undefined,
  );
  const purchaseFromAdvisorValue = getCorrectPaymentValue(
    UserAuthorizationLevel.PRODUCT_MANAGER,
    insuranceAdvice ? getCorrectPayment(insuranceAdvice) : undefined,
  );
  const adviceSlider = getFirst("AdviceSlider", adviceCardContent);
  const muiSlider = getFirst("MuiSlider-thumb", adviceSlider);
  const selectedSliderValue = Number(
    getFirst("input", muiSlider, true)?.attributes.getNamedItem("aria-valuenow")
      ?.textContent ?? "0",
  );

  const purchaseFromAdvisorText = getFirst(
    "div",
    getFirst("PurchaseFromAdvisorText", adviceCardContent),
    true,
  )?.textContent;

  const oneTimePaymentCheckbox = getOneTimePayment(
    adviceCardContent,
    insuranceAdvice,
  );

  return [
    {
      insurance,
      adviceCardHeader: {
        insuranceChecked,
        insuranceName,
        insuranceMonthlyPrice,
      },
      adviceCardContent: {
        adviceHeaderText,
        adviceTexts,
        advicedSliderValue,
        selectedSliderValue,
        ...(purchaseFromAdvisorText ? { purchaseFromAdvisorText } : {}),
        ...(purchaseFromAdvisorValue > advicedSliderValue
          ? { purchaseFromAdvisorValue }
          : {}),
        ...(oneTimePaymentCheckbox ? { oneTimePaymentCheckbox } : {}),
      },
    },
  ];
};

const getOneTimePayment = (
  adviceCardContent?: Element | HTMLInputElement,
  insuranceAdvice?: InsuranceAdvice,
): OneTimePayment | null => {
  const oneTimePaymentCheckbox = getFirst(
    "OneTimePaymentCheckbox",
    adviceCardContent,
  );
  if (!oneTimePaymentCheckbox) {
    return null;
  }
  const oneTimePaymentChecked = extractCheckboxValue(
    "OneTimePaymentCheckbox",
    adviceCardContent,
  );
  const oneTimePaymentText = getFirst("label", oneTimePaymentCheckbox, true)
    ?.lastElementChild?.textContent;
  let purchaseFromAdvisorValue = null;
  if (oneTimePaymentCheckbox) {
    const advicedPayment = getCorrectAdviceValues(
      insuranceAdvice?.singlePayment,
    ).adviceValue;
    const purchaseFromAdvisorPayment = getCorrectPaymentValue(
      UserAuthorizationLevel.PRODUCT_MANAGER,
      insuranceAdvice ? insuranceAdvice.singlePayment : undefined,
    );
    if (purchaseFromAdvisorPayment > advicedPayment) {
      purchaseFromAdvisorValue = purchaseFromAdvisorPayment;
    }
  }
  return {
    oneTimePaymentChecked,
    oneTimePaymentText: oneTimePaymentText ?? "",
    ...(purchaseFromAdvisorValue ? { purchaseFromAdvisorValue } : {}),
  };
};

const extractCheckboxValue = (name: string, element?: Element): boolean => {
  const checkbox = getFirst(name, element);
  const input = getFirst<HTMLInputElement>(
    'input[type="checkbox"]',
    checkbox,
    true,
  );
  return Boolean(input?.checked);
};

const extractAdviceDiffCardData = (ref: HTMLDivElement): AdviceDiffCard => {
  const adviceDiffCard = getFirst("AdviceDiffCard", ref);
  const texts = extractTextsByClassOrTagName("AdviceDiffText", adviceDiffCard);
  if (!adviceDiffCard) {
    return { texts, diffs: [] };
  }
  return {
    texts,
    diffs: [
      ...extractDiffMessages(
        INSURANCE_NAME_VALUE.DEATH,
        getFirst("DEATH_diffMessages", adviceDiffCard),
      ),
      ...extractDiffMessages(
        INSURANCE_NAME_VALUE.DISABILITY_FLEX,
        getFirst("DISABILITY_FLEX_diffMessages", adviceDiffCard),
      ),
      ...extractDiffMessages(
        INSURANCE_NAME_VALUE.CRITICAL_ILLNESS,
        getFirst("CRITICAL_ILLNESS_diffMessages", adviceDiffCard),
      ),
    ],
  };
};

const extractDiffMessages = (
  insurance: ADVICE_NAMES,
  element?: HTMLInputElement | Element,
): AdviceDiff[] => {
  if (!element) {
    return [];
  }
  const getDiff = (diffType: string): AdviceDiff[] =>
    Array.from(element.getElementsByClassName(diffType)).map((diff) => ({
      insurance,
      diffType,
      text: getFirst("p", diff, true)?.textContent ?? "",
    }));
  return [
    ...getDiff("AdviceDiffInfo"),
    ...getDiff("AdviceDiffUnavailable"),
    ...getDiff("AdviceDiffTrue"),
    ...getDiff("AdviceDiffFalse"),
  ];
};

const extractAdviceTotalPriceCardData = (
  ref: HTMLDivElement,
): AdviceTotalPriceCard => {
  const adviceTotalPriceCard = extractTextsByClassOrTagName(
    "div",
    getFirst("AdviceTotalPriceCard", ref),
    true,
  );
  return {
    numberOfInsurances: Number(
      (adviceTotalPriceCard[0].replace(/\s/g, "").match(/\d+/) ?? []).join(),
    ),
    totalMonthlyPrice: Number(
      (adviceTotalPriceCard[1].replace(/\s/g, "").match(/\d+/) ?? []).join(),
    ),
  };
};

const getFirst = <T extends Element>(
  name: string,
  e: Element | undefined,
  tag = false,
): T | undefined => {
  if (!isDefined(e)) {
    return undefined;
  }

  const element = e.querySelector<T>(tag ? name : `.${name}`);
  return isDefined(element) ? element : undefined;
};
